import React from "react"
import { ScrollReveal } from "../ui/scroll-reveal"
import { Button } from "../ui/button"
import { Linkedin, UserCog, BrainCircuit, Rocket, Lightbulb, Building, TrendingUp, Server, Cloud, Shield, Settings, Network, LineChart, Users, Briefcase, HeartHandshake, Globe } from "lucide-react"
import { EventCategory } from "../../lib/analytics"
import { safeTrackEvent } from "../../lib/analytics-utils"
import { useLocation } from "react-router-dom"

export function ConsultantsSection() {
  const location = useLocation();
  const pageName = location.pathname === "/" ? "home" : location.pathname.replace(/^\/|\/$/g, '').replace(/\//g, '_');

  const handleLinkedInClick = (profile: string) => {
    safeTrackEvent({
      category: EventCategory.Engagement,
      label: `engagement_${pageName}_linkedin_${profile}`,
      context: {
        sourcePage: location.pathname,
        profileName: profile,
        externalPlatform: 'linkedin'
      }
    });
  };

  return (
    <section className="py-16">
      <div className="container px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto space-y-16">
        {/* Jon Block */}
        <ScrollReveal>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 lg:items-start">
            <div className="space-y-6 max-w-xl">
              <h3 className="text-2xl font-bold">Jon Block</h3>
              <p className="text-lg font-medium text-primary">Founder & CEO</p>
              <p className="text-muted-foreground">
              Jon Block is the founder of Syllepsis, with over 25 years of experience delivering innovative solutions, scaling technology-led businesses and leading high-performing teams. His expertise blends strategic insight, rapid implementation and Knowledge Architecture methodology, helping organisations structure their institutional knowledge to unlock AI's full potential. Since 2015, Jon has led the implementation of advanced machine learning and AI technologies across multiple sectors, driving lasting transformation through hands-on execution and strategic leadership. Under his leadership Syllepsis pairs this methodological rigour with practical, results-focused delivery for clients navigating AI adoption.
              </p>

              <div className="space-y-4">
                <h4 className="font-medium">Areas of Expertise</h4>
                <div className="space-y-2">
                  {[
                    { text: "Knowledge Architecture & AI Strategy", icon: BrainCircuit },
                    { text: "AI Implementation & Integration", icon: Rocket },
                    { text: "Strategic Leadership & Team Development", icon: Lightbulb },
                    { text: "Scaling Operations & Organisations", icon: UserCog }
                  ].map((expertise, index) => {
                    const Icon = expertise.icon;
                    return (
                      <div key={index} className="flex items-center gap-3">
                        <Icon className="w-5 h-5 text-primary" />
                        <span>{expertise.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <Button
                variant="outline"
                asChild
                className="mt-4"
                onClick={() => handleLinkedInClick('jon')}
              >
                <a
                  href="https://www.linkedin.com/in/jonblock/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2"
                >
                  <Linkedin className="w-4 h-4" />
                  Connect on LinkedIn
                </a>
              </Button>
            </div>

            <div className="flex justify-center lg:justify-end">
              <div className="aspect-square relative rounded-lg overflow-hidden w-[280px] lg:mt-[76px]">
                <img
                  src="/images/jon.jpg"
                  alt="Jon Block"
                  className="object-cover w-full h-full filter grayscale-[90%] dark:grayscale-[90%]"
                  onError={(e) => {
                    console.error("Failed to load image:", e.currentTarget.src);
                    e.currentTarget.src = "https://placehold.co/300x300/f5f5f5/666?text=Jon+Block";
                  }}
                />
              </div>
            </div>
          </div>
        </ScrollReveal>

        {/* Jana Eisenstein */}
        <ScrollReveal delay={0.2}>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 lg:items-start">
            <div className="space-y-6 max-w-xl">
              <h3 className="text-2xl font-bold">Jana Eisenstein</h3>
              <p className="text-lg font-medium text-primary">Chief Commercial Officer</p>
              <p className="text-muted-foreground">
              Jana brings over 30 years of commercial leadership spanning management consulting, technology and high-growth businesses. She began her career in consulting at Deloitte and Gemini Consulting, leading M&A, restructuring and new-business launches across the telco and media sectors - the foundation for a career built on transforming and scaling companies. From there she held executive and leadership roles across the full spectrum, from small, fast-growth technology pioneers to global corporations including Microsoft and ProSiebenSat.1, with C-suite positions at digital technology and computer-vision companies with operations across Europe, Asia and North America. Strategic but hands-on, she pairs executive industry experience with a consultant's instinct for implementing and managing technology change and unlocking growth. At Syllepsis she leads commercial strategy, partnerships and growth.
              </p>

              <div className="space-y-4">
                <h4 className="font-medium">Areas of Expertise</h4>
                <div className="space-y-2">
                  {[
                    { text: "Commercial Strategy & Partnerships", icon: HeartHandshake },
                    { text: "Technology Platforms & SaaS", icon: Server },
                    { text: "Scaling High-Growth Businesses", icon: TrendingUp },
                    { text: "International Market Expansion", icon: Globe }
                  ].map((expertise, index) => {
                    const Icon = expertise.icon;
                    return (
                      <div key={index} className="flex items-center gap-3">
                        <Icon className="w-5 h-5 text-primary" />
                        <span>{expertise.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <Button
                variant="outline"
                asChild
                className="mt-4"
                onClick={() => handleLinkedInClick('jana')}
              >
                <a
                  href="https://www.linkedin.com/in/jana-eisenstein"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2"
                >
                  <Linkedin className="w-4 h-4" />
                  Connect on LinkedIn
                </a>
              </Button>
            </div>

            <div className="flex justify-center lg:justify-end">
              <div className="aspect-square relative rounded-lg overflow-hidden w-[280px] lg:mt-[76px]">
                <img
                  src="/images/jana.jpg"
                  alt="Jana Eisenstein"
                  className="object-cover w-full h-full filter grayscale-[90%] dark:grayscale-[90%]"
                  onError={(e) => {
                    console.error("Failed to load image:", e.currentTarget.src);
                    e.currentTarget.src = "https://placehold.co/300x300/f5f5f5/666?text=Jana+Eisenstein";
                  }}
                />
              </div>
            </div>
          </div>
        </ScrollReveal>

        {/* Jim Wolff */}
        <ScrollReveal delay={0.2}>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 lg:items-start">
            <div className="space-y-6 max-w-xl">
              <h3 className="text-2xl font-bold">Jim Wolff</h3>
              <p className="text-lg font-medium text-primary">Director, AI Knowledge Transformation</p>
              <p className="text-muted-foreground">
              Jim brings over 20 years of experience across consumer insight, marketing transformation and AI. As Global Head of Digital Consumerism at Diageo, he led the global development of digital consumer understanding, building search and social intelligence across 27 markets and multiple billion dollar brands. He also developed AI agents that helped brand and planning teams turn consumer evidence into better decisions. Earlier in his career he led digital marketing, communications and brand experience programmes across paid, owned and earned channels. At Syllepsis, Jim leads client Knowledge Architecture engagements, helping organisations structure their institutional knowledge and embed it in how their people and AI agents make decisions.
              </p>

              <div className="space-y-4">
                <h4 className="font-medium">Areas of Expertise</h4>
                <div className="space-y-2">
                  {[
                    { text: "Knowledge Architecture & AI Adoption", icon: BrainCircuit },
                    { text: "AI Agents & Decision Support", icon: Network },
                    { text: "Consumer Intelligence & Insight", icon: Lightbulb },
                    { text: "Marketing & Brand Transformation", icon: TrendingUp },
                    { text: "Search, Social & Digital Platforms", icon: Globe }
                  ].map((expertise, index) => {
                    const Icon = expertise.icon;
                    return (
                      <div key={index} className="flex items-center gap-3">
                        <Icon className="w-5 h-5 text-primary" />
                        <span>{expertise.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <Button
                variant="outline"
                asChild
                className="mt-4"
                onClick={() => handleLinkedInClick('jim')}
              >
                <a
                  href="https://www.linkedin.com/in/jimwolff/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2"
                >
                  <Linkedin className="w-4 h-4" />
                  Connect on LinkedIn
                </a>
              </Button>
            </div>

            <div className="flex justify-center lg:justify-end">
              <div className="aspect-square relative rounded-lg overflow-hidden w-[280px] lg:mt-[76px]">
                <img
                  src="/images/jim.jpg"
                  alt="Jim Wolff"
                  className="object-cover w-full h-full filter grayscale-[90%] dark:grayscale-[90%]"
                  onError={(e) => {
                    console.error("Failed to load image:", e.currentTarget.src);
                    e.currentTarget.src = "https://placehold.co/300x300/f5f5f5/666?text=Jim+Wolff";
                  }}
                />
              </div>
            </div>
          </div>
        </ScrollReveal>

        {/* Indira Kuriyan */}
        <ScrollReveal delay={0.4}>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 lg:items-start">
            <div className="space-y-6 max-w-xl">
              <h3 className="text-2xl font-bold">Indira Kuriyan</h3>
              <p className="text-lg font-medium text-primary">Operating Partner</p>
              <p className="text-muted-foreground">
              Indira is a strategic business operations and commercial leader with over 20 years of experience across high-growth technology, media and consumer businesses such as ITV, Condé Nast and LVMH. At ITV she led one of the broadcaster's addressable advertising businesses and its P&L, taking it from a managed service to a fully SaaS model. Across Videology and Amobee she built a global commercial operations function and led commercial strategy, organisational design and post-acquisition integration across EMEA and APAC. An INSEAD MBA who brings rigour, structure and profitable growth to fast-changing organisations, Indira advises Syllepsis on commercial strategy, operational scaling and the practical application of AI.
              </p>

              <div className="space-y-4">
                <h4 className="font-medium">Areas of Expertise</h4>
                <div className="space-y-2">
                  {[
                    { text: "Commercial Strategy & Revenue Operations", icon: LineChart },
                    { text: "Business Operations & Organisational Design", icon: Users },
                    { text: "Scaling & Business Transformation", icon: TrendingUp },
                    { text: "AI Adoption in Commercial & Operational Functions", icon: BrainCircuit },
                    { text: "P&L Leadership & SaaS Business Models", icon: Briefcase }
                  ].map((expertise, index) => {
                    const Icon = expertise.icon;
                    return (
                      <div key={index} className="flex items-center gap-3">
                        <Icon className="w-5 h-5 text-primary" />
                        <span>{expertise.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <Button
                variant="outline"
                asChild
                className="mt-4"
                onClick={() => handleLinkedInClick('indira')}
              >
                <a
                  href="https://www.linkedin.com/in/indira-kuriyan"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2"
                >
                  <Linkedin className="w-4 h-4" />
                  Connect on LinkedIn
                </a>
              </Button>
            </div>

            <div className="flex justify-center lg:justify-end">
              <div className="aspect-square relative rounded-lg overflow-hidden w-[280px] lg:mt-[76px]">
                <img
                  src="/images/indira.jpg"
                  alt="Indira Kuriyan"
                  className="object-cover w-full h-full filter grayscale-[90%] dark:grayscale-[90%]"
                  onError={(e) => {
                    console.error("Failed to load image:", e.currentTarget.src);
                    e.currentTarget.src = "https://placehold.co/300x300/f5f5f5/666?text=Indira+Kuriyan";
                  }}
                />
              </div>
            </div>
          </div>
        </ScrollReveal>

        {/* John Rogers */}
        <ScrollReveal delay={0.6}>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 lg:items-start">
            <div className="space-y-6 max-w-xl">
              <h3 className="text-2xl font-bold">John Rogers</h3>
              <p className="text-lg font-medium text-primary">Technical Partner</p>
              <p className="text-muted-foreground">
              John brings over 25 years of experience building production software systems handling sensitive data at scale. As founder of Delosis, he created Psytools - a platform used by 50+ universities worldwide supporting millions of research assessments for clients including the Wellcome Trust, MRC, NIH and European Commission. His expertise spans full-stack development, cloud architecture, security and compliance. At Syllepsis, John advises on technology strategy and architecture, ensuring enterprise AI implementations meet the highest standards of security, scalability and reliability.
              </p>

              <div className="space-y-4">
                <h4 className="font-medium">Areas of Expertise</h4>
                <div className="space-y-2">
                  {[
                    { text: "Production Systems at Scale", icon: Server },
                    { text: "Cloud Architecture & Infrastructure", icon: Cloud },
                    { text: "Data Security & Regulatory Compliance", icon: Shield },
                    { text: "Technical Delivery & Quality Assurance", icon: Settings },
                    { text: "Enterprise System Integration", icon: Network }
                  ].map((expertise, index) => {
                    const Icon = expertise.icon;
                    return (
                      <div key={index} className="flex items-center gap-3">
                        <Icon className="w-5 h-5 text-primary" />
                        <span>{expertise.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <Button
                variant="outline"
                asChild
                className="mt-4"
                onClick={() => handleLinkedInClick('john')}
              >
                <a
                  href="https://www.linkedin.com/in/john-rogers-3a64545/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2"
                >
                  <Linkedin className="w-4 h-4" />
                  Connect on LinkedIn
                </a>
              </Button>
            </div>

            <div className="flex justify-center lg:justify-end">
              <div className="aspect-square relative rounded-lg overflow-hidden w-[280px] lg:mt-[76px]">
                <img
                  src="/images/john.jpg"
                  alt="John Rogers"
                  className="object-cover w-full h-full filter grayscale-[90%] dark:grayscale-[90%]"
                  onError={(e) => {
                    console.error("Failed to load image:", e.currentTarget.src);
                    e.currentTarget.src = "https://placehold.co/300x300/f5f5f5/666?text=John+Rogers";
                  }}
                />
              </div>
            </div>
          </div>
        </ScrollReveal>

        {/* Jeremy Taylor */}
        <ScrollReveal delay={0.8}>
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 lg:items-start">
            <div className="space-y-6 max-w-xl">
              <h3 className="text-2xl font-bold">Jeremy Taylor</h3>
              <p className="text-lg font-medium text-primary">Consulting Partner</p>
              <p className="text-muted-foreground">
              Jeremy brings over 30 years of experience delivering complex projects and leading businesses across energy, infrastructure and technology sectors. A Chartered Engineer and Fellow, he has managed multi-billion-dollar projects and transformed business performance across diverse environments, with P&L responsibility ranging from £50 million to over £1 billion. His experience spans hyperscale datacentre development, large-scale infrastructure operations and government transformation - including COO at FCDO Services, delivering secure services to the UK government across 168 countries. Jeremy's deep understanding of complex project delivery brings valuable sector expertise to Syllepsis clients.
              </p>

              <div className="space-y-4">
                <h4 className="font-medium">Areas of Expertise</h4>
                <div className="space-y-2">
                  {[
                    { text: "Infrastructure & Energy Project Delivery", icon: Building },
                    { text: "Datacentre Development & Operations", icon: Server },
                    { text: "Government & Public Sector Transformation", icon: Shield },
                    { text: "Operational Excellence & Business Turnaround", icon: TrendingUp }
                  ].map((expertise, index) => {
                    const Icon = expertise.icon;
                    return (
                      <div key={index} className="flex items-center gap-3">
                        <Icon className="w-5 h-5 text-primary" />
                        <span>{expertise.text}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              <Button
                variant="outline"
                asChild
                className="mt-4"
                onClick={() => handleLinkedInClick('jeremy')}
              >
                <a
                  href="https://www.linkedin.com/in/jeremy-taylor-jt"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2"
                >
                  <Linkedin className="w-4 h-4" />
                  Connect on LinkedIn
                </a>
              </Button>
            </div>

            <div className="flex justify-center lg:justify-end">
              <div className="aspect-square relative rounded-lg overflow-hidden w-[280px] lg:mt-[76px]">
                <img
                  src="/images/jeremy.jpg"
                  alt="Jeremy Taylor"
                  className="object-cover w-full h-full filter grayscale-[90%] dark:grayscale-[90%]"
                  onError={(e) => {
                    console.error("Failed to load image:", e.currentTarget.src);
                    e.currentTarget.src = "https://placehold.co/300x300/f5f5f5/666?text=Jeremy+Taylor";
                  }}
                />
              </div>
            </div>
          </div>
        </ScrollReveal>
      </div>
    </section>
  )
}
